import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckboxSquare } from 'shared/components/ui/checkboxSquare';
import colorsStore from 'shared/store/colorsStore';

type ReportFormat = string;

interface ReportSubSection {
  visible: boolean;
  selected: boolean;
  title: string;
  formats: ReportFormat[];
}

export interface ReportSection {
  visible: boolean;
  selected: boolean;
  editable?: boolean;
  title: string;
  formats?: ReportFormat[];
  [key: string]: boolean | string | ReportFormat[] | ReportSubSection | undefined;
}

export interface ReportSections {
  [key: string]: ReportSection;
}

const isReportSubSection = (value: any): value is ReportSubSection => {
  return value && typeof value === 'object' && 'visible' in value && 'selected' in value && 'formats' in value;
};

interface ReportSectionTreeProps {
  sections: ReportSections;
  onChange: (sections: ReportSections) => void;
  showCosts: boolean;
  onShowCostsChange: (checked: boolean) => void;
  showIsin: boolean;
  onShowIsinChange: (checked: boolean) => void;
}

export const ReportSectionsTree: React.FC<ReportSectionTreeProps> = ({
  sections,
  onChange,
  showCosts,
  onShowCostsChange,
  showIsin,
  onShowIsinChange,
}) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [selectedSections, setSelectedSections] = useState<Record<string, boolean>>({});
  const [selectedSubSections, setSelectedSubSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const initSections: Record<string, boolean> = {};
    const initSubSections: Record<string, boolean> = {};

    Object.entries(sections).forEach(([sectionKey, section]) => {
      initSections[sectionKey] = section.selected;

      Object.entries(section).forEach(([key, value]) => {
        if (isReportSubSection(value)) {
          const subSectionKey = `${sectionKey}.${key}`;
          initSubSections[subSectionKey] = value.selected;
        }
      });
    });

    setSelectedSections(initSections);
    setSelectedSubSections(initSubSections);
  }, [sections]);

  const handleSectionChange = (sectionKey: string, checked: boolean) => {
    // If section is explicitly not editable, don't allow changes
    if (sections[sectionKey].editable === false) {
      return;
    }

    const newSelectedSections = { ...selectedSections, [sectionKey]: checked };
    const newSelectedSubSections = { ...selectedSubSections };

    // Update all non-hidden subsections when parent is changed.
    // «Espandi Wrapper» (issue #90) is an opt-in child of the funds table:
    // checking the parent must not silently arm it, but unchecking the parent
    // clears it like everything else (otherwise it gets stuck checked while
    // disabled and re-arms itself when the funds table comes back).
    Object.entries(sections[sectionKey]).forEach(([key, value]) => {
      if (isReportSubSection(value) && value.visible && (!checked || key !== 'espandi_wrapper')) {
        const subSectionKey = `${sectionKey}.${key}`;
        newSelectedSubSections[subSectionKey] = checked;
      }
    });

    setSelectedSections(newSelectedSections);
    setSelectedSubSections(newSelectedSubSections);
    updateParentSections(newSelectedSections, newSelectedSubSections);
  };

  const handleSubSectionChange = (sectionKey: string, subSectionKey: string, checked: boolean) => {
    // If parent section is explicitly not editable, don't allow changes
    if (sections[sectionKey].editable === false) {
      return;
    }

    const fullKey = `${sectionKey}.${subSectionKey}`;
    const newSelectedSubSections = {
      ...selectedSubSections,
      [fullKey]: checked,
    };

    // «Espandi Wrapper» rides on the funds table: deselecting the table also
    // clears it, so it can never sit checked-but-disabled (issue #90).
    if (subSectionKey === 'fondi' && !checked) {
      newSelectedSubSections[`${sectionKey}.espandi_wrapper`] = false;
    }

    // Check if any subsections are selected for this section
    const hasSelectedSubSections = Object.entries(newSelectedSubSections).some(
      ([key, value]) => key.startsWith(sectionKey) && value,
    );

    const newSelectedSections = {
      ...selectedSections,
      [sectionKey]: hasSelectedSubSections,
    };

    setSelectedSections(newSelectedSections);
    setSelectedSubSections(newSelectedSubSections);
    updateParentSections(newSelectedSections, newSelectedSubSections);
  };

  const updateParentSections = (
    selectedSections: Record<string, boolean>,
    selectedSubSections: Record<string, boolean>,
  ) => {
    // Create a deep copy of the original sections
    const updatedSections: ReportSections = JSON.parse(JSON.stringify(sections));

    // Update main sections visibility
    Object.keys(selectedSections).forEach((sectionKey) => {
      if (updatedSections[sectionKey]) {
        updatedSections[sectionKey].selected = selectedSections[sectionKey];
      }
    });

    // Update subsections visibility
    Object.entries(selectedSubSections).forEach(([fullKey, isSelected]) => {
      const [sectionKey, subSectionKey] = fullKey.split('.');
      if (updatedSections[sectionKey] && isReportSubSection(updatedSections[sectionKey][subSectionKey])) {
        (updatedSections[sectionKey][subSectionKey] as ReportSubSection).selected = isSelected;
      }
    });

    onChange(updatedSections);
  };

  const formatList = (formats: ReportFormat[]) => {
    return formats.length > 0 ? `(${formats.join(', ')})` : '';
  };

  return (
    <div className="h-[300px] overflow-y-auto pr-4">
      <div className="space-y-4">
        {/* General Settings section */}
        <div className="space-y-2">
          <span className="font-medium" style={{ color: colorsConfig.finecoBlack }}>
            {t('generateReport.generalSettings')}
          </span>

          <div className="ml-6 space-y-1" style={{ color: colorsConfig.finecoBlack }}>
            <div className="flex items-center space-x-2">
              <CheckboxSquare
                checked={showCosts}
                onCheckedChange={(checked) => onShowCostsChange(checked as boolean)}
              />
              <span className="text-sm">{t('generateReport.showCosts')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckboxSquare checked={showIsin} onCheckedChange={(checked) => onShowIsinChange(checked as boolean)} />
              <span className="text-sm">{t('generateReport.showIsin')}</span>
            </div>
          </div>
        </div>

        {/* Horizontal separator */}
        <hr className="border-gray-300" />

        {Object.entries(sections).map(([sectionKey, section]) => {
          if (!section.visible) {
            return null;
          }

          return (
            <div key={sectionKey} className="space-y-2">
              <div
                className="flex items-center space-x-2"
                style={{ color: colorsConfig.finecoBlack, opacity: section.editable === false ? 0.3 : 1 }}
              >
                <CheckboxSquare
                  checked={selectedSections[sectionKey]}
                  onCheckedChange={(checked) => handleSectionChange(sectionKey, checked as boolean)}
                  disabled={section.editable === false}
                />
                <span className="font-medium">
                  {section.title}
                  {section.formats && section.formats.length > 0 && (
                    <span className="text-xs font-normal text-gray-500"> {formatList(section.formats)}</span>
                  )}
                </span>
              </div>

              <div
                className="ml-6 space-y-1"
                style={{ color: colorsConfig.finecoBlack, opacity: section.editable === false ? 0.3 : 1 }}
              >
                {Object.entries(section).map(([key, value]) => {
                  if (isReportSubSection(value)) {
                    if (!value.visible) {
                      return null;
                    }

                    const subSectionKey = `${sectionKey}.${key}`;
                    // «Espandi Wrapper» (issue #90) renders as a third level
                    // under the funds table and needs that table selected.
                    const isEspandiWrapper = key === 'espandi_wrapper';
                    const espandiDisabled = isEspandiWrapper && !selectedSubSections[`${sectionKey}.fondi`];
                    return (
                      <div key={key} className={`flex items-center space-x-2${isEspandiWrapper ? ' ml-6' : ''}`}>
                        <CheckboxSquare
                          checked={selectedSubSections[subSectionKey]}
                          onCheckedChange={(checked) => handleSubSectionChange(sectionKey, key, checked as boolean)}
                          disabled={section.editable === false || espandiDisabled}
                        />
                        <span className="text-sm" style={espandiDisabled ? { opacity: 0.4 } : undefined}>
                          {value.title} <span className="text-xs text-gray-500">{formatList(value.formats)}</span>
                        </span>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
